import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export interface ImageZoomLightboxProps {
  open: boolean;
  images: string[];
  index: number;
  alt: string;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

// Fullscreen click-to-zoom viewer for the product gallery. Mirrors Modal's
// focus-trap/Escape/return-focus handling, but full-bleed instead of a
// centered card — a lightbox wants the image large, not boxed in.
export function ImageZoomLightbox({ open, images, index, alt, onClose, onNavigate }: ImageZoomLightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      triggerRef.current?.focus();
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && images.length > 1) onNavigate((index - 1 + images.length) % images.length);
      if (e.key === "ArrowRight" && images.length > 1) onNavigate((index + 1) % images.length);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, onNavigate, index, images.length]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={`${alt} — zoomed image`}
          tabIndex={-1}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 outline-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close zoomed image"
            className="absolute top-4 right-4 h-11 w-11 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-300"
          >
            <X size={22} />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate((index - 1 + images.length) % images.length);
                }}
                aria-label="Previous image"
                className="absolute left-2 sm:left-4 h-11 w-11 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-300"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate((index + 1) % images.length);
                }}
                aria-label="Next image"
                className="absolute right-2 sm:right-4 h-11 w-11 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-300"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          <motion.img
            key={images[index]}
            src={images[index]}
            alt={alt}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="max-w-[92vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
